import * as Sentry from '@sentry/react';
import { scrubEvent, scrubBreadcrumb, scrubRecordingEvent, shouldReplay } from './utils/sentryScrub.js';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // The client portal contains client, staff, invoice, and guest context.
      // Error replay may help diagnose a release, but it must never capture the
      // visible text or media that made the page useful to that client.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Token routes (hook, join/schedule, claims, sign, …) never record; other events are URL-scrubbed.
        beforeErrorSampling: () => shouldReplay(window.location.pathname),
        beforeAddRecordingEvent: scrubRecordingEvent,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    enabled: import.meta.env.PROD,
    // Bearer tokens in paths/query strings (/certification/hook/<token>, ?token=, ?code=, token_hash) → scrubbed.
    beforeSend: (event) => scrubEvent(event),
    beforeSendTransaction: (event) => scrubEvent(event),
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  });
  Sentry.addEventProcessor((event) => scrubEvent(event)); // also covers replay_event urls
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.jsx';
import { createScopedLogger } from './utils/logger.js';
import { supabase } from './lib/supabase.js';
import SupabaseFactory from '@bricksOS/shared/SupabaseFactory.js';
const log = createScopedLogger('main');

// SECURITY: Admin client no longer exposed to browser for security

// Initialize SupabaseFactory for BricksOS services
// Admin client will be lazy-loaded only when first requested
SupabaseFactory.setClient(supabase);
log.info?.('SupabaseFactory initialized for BricksOS services');

function isChunkLoadError(errorMessage, errorSource = '') {
  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('error loading dynamically imported module') ||
    errorMessage.includes('Importing a module script failed') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('ChunkLoadError') ||
    errorSource.includes('.bundle.js')
  );
}

function attemptChunkReload(label) {
  // Only ever reload ONCE per tab session — boolean flag, not timestamp.
  // A timestamp window re-fires after expiry, which is what caused the loop.
  const reloadKey = 'chunk_error_reloaded';
  if (sessionStorage.getItem(reloadKey)) {
    log.warn?.(`${label}: already reloaded once this session, skipping to avoid loop`);
    return;
  }
  sessionStorage.setItem(reloadKey, 'true');
  log.warn?.(`${label}: chunk load failure detected, reloading once for fresh assets`);

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
  }
  if ('caches' in window) {
    caches.keys().then(names => names.forEach(name => caches.delete(name)));
  }

  setTimeout(() => window.location.reload(true), 500);
}

// Handle chunk load errors from synchronous script errors
window.addEventListener('error', (event) => {
  const error = event.error || event.message || '';
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  const errorSource = event.filename || event.target?.src || '';

  if (isChunkLoadError(errorMessage, errorSource)) {
    attemptChunkReload('error-event');
    event.preventDefault();
  }
});

// Handle chunk load errors from dynamic import() failures
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason || {};
  const errorMessage = typeof error === 'string' ? error : error?.message || '';

  if (isChunkLoadError(errorMessage)) {
    attemptChunkReload('unhandledrejection');
    event.preventDefault();
  }
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  log.error?.('Root element not found');
  throw new Error('Root element not found');
}

const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <App />
  </StrictMode>
);

log.info?.('Application bootstrap complete');
